import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { getDashboardStats, getVisitors } from '@/services/api';
import { Shield, UserCheck, Users, AlertTriangle, RefreshCw, Dumbbell, Calendar, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const SecurityHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const [statsData, visitorsData] = await Promise.all([
        getDashboardStats(),
        getVisitors()
      ]);
      setStats(statsData);
      setRecentVisitors(visitorsData.slice(0, 5));
      setLastUpdated(new Date());
      if (showToast) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to refresh data');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchData]);

  const visitorStatCards = [
    {
      title: 'Pending Visitors',
      value: stats?.pending_visitors || 0,
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Checked In',
      value: stats?.checked_in_visitors || 0,
      icon: UserCheck,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Checked Out',
      value: stats?.checked_out_visitors || 0,
      icon: LogOut,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100'
    },
    {
      title: "Today's Visitors",
      value: stats?.todays_visitors || 0,
      icon: Shield,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    }
  ];

  const amenityStatCards = [
    {
      title: 'Pending Check-In',
      value: stats?.pending_checkins || 0,
      icon: Dumbbell,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      title: 'Checked In',
      value: stats?.amenity_checked_in || 0,
      icon: UserCheck,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    },
    {
      title: 'Checked Out',
      value: stats?.amenity_checked_out || 0,
      icon: LogOut,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Security Dashboard</h1>
          <p className="text-sm md:text-base text-slate-600">Monitor and manage visitor access</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={isRefreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Visitor Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Visitor Management</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {visitorStatCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="stat-card cursor-pointer border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                onClick={() => navigate('/security/visitors')}
                data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl md:text-4xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                    <div className={`h-12 w-12 md:h-14 md:w-14 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 md:h-7 md:w-7 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Amenity Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Amenity Check-In</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {amenityStatCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="stat-card cursor-pointer border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                onClick={() => navigate('/security/amenities')}
                data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl md:text-4xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                    <div className={`h-12 w-12 md:h-14 md:w-14 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 md:h-7 md:w-7 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">Recent Visitor Activity</h2>
          {recentVisitors.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No recent visitors</p>
          ) : (
            <div className="space-y-3">
              {recentVisitors.map((visitor) => (
                <div
                  key={visitor.id}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid="recent-visitor-item"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{visitor.name}</p>
                      <p className="text-sm text-slate-600">Host: {visitor.host_name} | Unit: {visitor.host_unit}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${
                        visitor.status === 'pending'
                          ? 'bg-orange-100 text-orange-700'
                          : visitor.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : visitor.status === 'checked_in'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {visitor.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert Banner */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-semibold text-slate-900">Security Status: All Clear</p>
              <p className="text-sm text-slate-600">All systems operational. No active alerts.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityHome;